// src/config/system-structure.ts
// Configuración de la estructura modular del sistema
// Define módulos → menús → botones principales → acciones de fila

/**
 * GRANULARIDAD DE PERMISOS:
 * - 'full': Si el menú está visible, se generan automáticamente todos los permisos
 *   (botones principales, acciones de fila, etc.). Usado para menús simples donde
 *   no se requiere control granular de permisos.
 * - 'menu-only': Solo se genera el permiso de visibilidad del menú.
 *   Usado para menús de solo lectura o reportes donde no hay acciones adicionales.
 * - 'custom' (default): Se procesan individualmente cada permiso según la selección
 *   del usuario en el wizard. Usado para menús que requieren control granular.
 * - 'bundle-add': Solo lectura con menú visible (+ Ver fila) o gestión completa si "Añadir" está activo.
 *
 * MENÚS CON GRANULARIDAD ESPECIAL:
 * - product-catalog: 'bundle-add' - Visible = solo lectura; Añadir = todas las acciones del catálogo
 * - inventory-register: 'custom' - Acciones de fila y botones (incl. etiquetas) configurables por rol
 * - product-history: 'menu-only' - Solo visibilidad del menú
 */

export interface ButtonConfig {
  id: string;
  name: string;
  action: string;
  icon?: string;
}

export interface ActionConfig {
  id: string;
  name: string;
  action: string;
  icon?: string;
}

export interface FlagConfig {
  id: string;
  name: string;
  action: string;
  icon?: string;
  description?: string;
}

export interface MenuConfig {
  id: string;
  name: string;
  path: string;
  order: number;
  /**
   * Configuración de granularidad de permisos para este menú
   * - 'full': Genera todos los permisos automáticamente si el menú está visible
   * - 'menu-only': Solo genera permiso de visibilidad del menú
   * - 'custom': Procesa cada permiso individualmente (default)
   * - 'bundle-add': Wizard con Añadir = gestión completa (resto de permisos se expanden al guardar)
   */
  granularity?: 'full' | 'menu-only' | 'custom' | 'bundle-add';
  /**
   * Menús granulados (custom): si el menú está visible, la acción de fila "Ver"
   * (view_row) se asigna siempre. Activar en nuevos menús custom al extender el piloto.
   */
  bindViewRowToMenuVisibility?: boolean;
  mainButtons?: ButtonConfig[];
  rowActions?: ActionConfig[];
  columnFlags?: FlagConfig[];
  /** Permisos del flujo (ej. nueva venta, transferencias), agrupados aparte de botones/fila */
  salesFlowActions?: FlagConfig[];
  /** Título del bloque de flujos en el wizard de roles (ej. "Flujo de venta", "Flujos") */
  flowSectionTitle?: string;
}

export interface ModuleConfig {
  id: string;
  name: string;
  icon?: string;
  order: number;
  menus: MenuConfig[];
}

/**
 * Estructura completa del sistema
 * Basada en el sidebar actual del DashboardLayout
 */
export const SYSTEM_STRUCTURE: ModuleConfig[] = [
  {
    id: 'home',
    name: 'Inicio',
    icon: 'Home',
    order: 0,
    menus: [
      {
        id: 'dashboard',
        name: 'Dashboard',
        path: '/dashboard',
        order: 1,
        // Binary for now: see the whole home dashboard or not.
        // Widget-level granularity is deferred until the client requests it.
        granularity: 'menu-only',
      },
    ],
  },
  {
    id: 'products',
    name: 'Productos',
    icon: 'Inventory',
    order: 1,
    menus: [
      {
        id: 'product-catalog',
        name: 'Catálogo de Productos',
        path: '/dashboard/products',
        order: 1,
        granularity: 'bundle-add',
        bindViewRowToMenuVisibility: true,
        mainButtons: [
          {
            id: 'add',
            name: 'Añadir (gestión completa)',
            action: 'add',
            icon: 'Add',
          },
        ],
      },
    ],
  },
  {
    id: 'inventory',
    name: 'Inventario',
    icon: 'Assignment',
    order: 2,
    menus: [
      {
        id: 'inventory-register',
        name: 'Registro de Inventario',
        path: '/dashboard/inventory/register',
        order: 1,
        granularity: 'custom',
        bindViewRowToMenuVisibility: true,
        mainButtons: [
          { id: 'add', name: 'Añadir', action: 'add', icon: 'Add' },
          { id: 'export', name: 'Exportar', action: 'export', icon: 'CloudDownload' },
        ],
        rowActions: [
          { id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' },
          { id: 'edit', name: 'Editar', action: 'edit', icon: 'Edit' },
          { id: 'cancel', name: 'Cancelar', action: 'cancel', icon: 'Cancel' },
          {
            id: 'print',
            name: 'Imprimir detalle',
            action: 'print',
            icon: 'Print',
          },
          {
            id: 'label_print',
            name: 'Imprimir etiquetas',
            action: 'label_print',
            icon: 'Receipt',
          },
        ],
      },
      {
        id: 'inventory-warehouse',
        name: 'Gestión en Almacén',
        path: '/dashboard/inventory/query',
        order: 2,
        bindViewRowToMenuVisibility: true,
        mainButtons: [
          { id: 'edit', name: 'Editar', action: 'edit', icon: 'Edit' },
          // Botón contextual: aparece cuando hay piezas seleccionadas (Transferir n)
          { id: 'transfer', name: 'Transferir', action: 'transfer', icon: 'SwapHoriz' },
        ],
        rowActions: [
          { id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' },
          { id: 'reserve', name: 'Bloquear', action: 'reserve', icon: 'Lock' },
          {
            id: 'unreserve',
            name: 'Liberar bloqueo',
            action: 'unreserve',
            icon: 'LockOpen',
          },
        ],
        // Flags/Componentes del dashboard (InventoryStats) para control granular por rol
        columnFlags: [
          {
            id: 'kpi_total_products',
            name: 'KPI: Total piezas',
            action: 'kpi_total_products',
            icon: 'Inventory',
            description: 'Tarjeta KPI: Total piezas',
          },
          {
            id: 'kpi_total_sale_value',
            name: 'KPI: Valor de Venta',
            action: 'kpi_total_sale_value',
            icon: 'AttachMoney',
            description: 'Tarjeta KPI: Valor de Venta',
          },
          {
            id: 'kpi_total_cost_value',
            name: 'KPI: Valor a Costo Unitario',
            action: 'kpi_total_cost_value',
            icon: 'AttachMoney',
            description: 'Tarjeta KPI: Valor a Costo Unitario',
          },
          {
            id: 'kpi_unassigned',
            name: 'KPI: En almacén',
            action: 'kpi_unassigned',
            icon: 'Warehouse',
            description: 'Tarjeta KPI: En almacén',
          },
          {
            id: 'kpi_assigned',
            name: 'KPI: En sucursal',
            action: 'kpi_assigned',
            icon: 'Store',
            description: 'Tarjeta KPI: En sucursal',
          },
          {
            id: 'kpi_in_transit',
            name: 'KPI: En Tránsito',
            action: 'kpi_in_transit',
            icon: 'LocalShipping',
            description: 'Tarjeta KPI: En Tránsito',
          },
          {
            id: 'kpi_bajas',
            name: 'KPI: Bajas',
            action: 'kpi_bajas',
            icon: 'HighlightOff',
            description: 'Tarjeta KPI: piezas dadas de baja',
          },
          {
            id: 'kpi_warehouse_reserved',
            name: 'KPI: Bloqueadas en almacén',
            action: 'kpi_warehouse_reserved',
            icon: 'Lock',
            description: 'Tarjeta KPI: piezas bloqueadas en almacén o tránsito',
          },
        ],
      },
      {
        id: 'inventory-transfer',
        name: 'Transferencia de Inventario',
        path: '/dashboard/inventory/transfer',
        order: 3,
        bindViewRowToMenuVisibility: true,
        flowSectionTitle: 'Flujos',
        salesFlowActions: [
          {
            id: 'toggle_require_counting',
            name: 'Configurar requisito de conteo manual',
            action: 'toggle_require_counting',
            icon: 'FormatListNumbered',
            description:
              'Permite activar o desactivar el conteo manual antes de aceptar una transferencia.',
          },
        ],
        rowActions: [
          { id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' },
          { id: 'approve', name: 'Aprobar', action: 'approve', icon: 'CheckCircle' },
          { id: 'reject', name: 'Rechazar', action: 'reject', icon: 'Cancel' },
        ],
      },
      {
        id: 'inventory-request',
        name: 'Solicitudes de inventario',
        path: '/dashboard/inventory/requests',
        order: 4,
        granularity: 'custom',
        rowActions: [
          { id: 'approve', name: 'Aprobar/Rechazar', action: 'approve', icon: 'CheckCircle' },
        ],
      },
      {
        id: 'inventory-branch',
        name: 'Gestión en Sucursal',
        path: '/dashboard/inventory/branch-query',
        order: 5,
        granularity: 'custom',
        bindViewRowToMenuVisibility: true,
        mainButtons: [
          { id: 'transfer', name: 'Transferir', action: 'transfer', icon: 'SwapHoriz' },
        ],
        rowActions: [
          { id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' },
          { id: 'edit', name: 'Editar', action: 'edit', icon: 'Edit' },
          { id: 'reserve', name: 'Bloquear', action: 'reserve', icon: 'Lock' },
          { id: 'unreserve', name: 'Liberar bloqueo', action: 'unreserve', icon: 'LockOpen' },
        ],
        columnFlags: [
          {
            id: 'kpi_total_products',
            name: 'KPI: Total piezas',
            action: 'kpi_total_products',
            icon: 'Inventory',
            description: 'Tarjeta KPI: Total piezas en sucursal',
          },
          {
            id: 'kpi_total_sale_value',
            name: 'KPI: Valor de Venta',
            action: 'kpi_total_sale_value',
            icon: 'AttachMoney',
            description: 'Tarjeta KPI: Valor de Venta',
          },
          {
            id: 'kpi_total_cost_value',
            name: 'KPI: Valor a Costo Unitario',
            action: 'kpi_total_cost_value',
            icon: 'AttachMoney',
            description: 'Tarjeta KPI: Valor a Costo Unitario en sucursal',
          },
          {
            id: 'kpi_bajas',
            name: 'KPI: Bajas',
            action: 'kpi_bajas',
            icon: 'HighlightOff',
            description: 'Tarjeta KPI: piezas dadas de baja en sucursal',
          },
        ],
      },
      {
        id: 'inventory-general',
        name: 'Inventario general',
        path: '/dashboard/inventory/general',
        order: 6,
        granularity: 'custom',
        bindViewRowToMenuVisibility: true,
        flowSectionTitle: 'Flujos',
        salesFlowActions: [
          {
            id: 'create',
            name: 'Crear solicitud',
            action: 'create',
            icon: 'Add',
            description: 'Seleccionar piezas y crear solicitudes de inventario (SOL)',
          },
        ],
        rowActions: [{ id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' }],
        columnFlags: [
          {
            id: 'kpi_total_products',
            name: 'KPI: Total piezas',
            action: 'kpi_total_products',
            icon: 'Inventory',
            description: 'Tarjeta KPI: Total piezas',
          },
          {
            id: 'kpi_catalog_product_total',
            name: 'KPI: Total Productos',
            action: 'kpi_catalog_product_total',
            icon: 'Apps',
            description: 'Total de productos en catálogo',
          },
          {
            id: 'kpi_total_sale_value',
            name: 'KPI: Valor de Venta',
            action: 'kpi_total_sale_value',
            icon: 'AttachMoney',
            description: 'Tarjeta KPI: Valor de Venta',
          },
          {
            id: 'kpi_total_cost_value',
            name: 'KPI: Valor a Costo Unitario',
            action: 'kpi_total_cost_value',
            icon: 'AttachMoney',
            description: 'Tarjeta KPI: Valor a Costo Unitario (Inventario general)',
          },
          {
            id: 'kpi_unassigned',
            name: 'KPI: En almacén',
            action: 'kpi_unassigned',
            icon: 'Warehouse',
            description: 'Tarjeta KPI: En almacén',
          },
          {
            id: 'kpi_assigned',
            name: 'KPI: En sucursal',
            action: 'kpi_assigned',
            icon: 'Store',
            description: 'Tarjeta KPI: En sucursal',
          },
          {
            id: 'kpi_in_transit',
            name: 'KPI: En Tránsito',
            action: 'kpi_in_transit',
            icon: 'LocalShipping',
            description: 'Tarjeta KPI: En Tránsito',
          },
          {
            id: 'kpi_bajas',
            name: 'KPI: Bajas',
            action: 'kpi_bajas',
            icon: 'HighlightOff',
            description: 'Tarjeta KPI: piezas dadas de baja',
          },
        ],
      },
      {
        id: 'safety-stock',
        name: 'Stock de seguridad',
        path: '/dashboard/inventory/safety-stock',
        order: 6,
        mainButtons: [
          {
            id: 'edit',
            name: 'Edición (masiva y por fila)',
            action: 'edit',
            icon: 'Edit',
          },
        ],
      },
      {
        id: 'physical-count',
        name: 'Control de Inventario Físico',
        path: '/dashboard/inventory/physical-count',
        order: 7,
        flowSectionTitle: 'Tras finalizar sesión',
        salesFlowActions: [
          {
            id: 'write_off_missing',
            name: 'Dar de baja faltantes de conteo',
            action: 'write_off_missing',
            icon: 'Inventory2',
            description:
              'Permite dar de baja en inventario las piezas marcadas como faltantes (individual o todas en bloque).',
          },
        ],
        mainButtons: [
          { id: 'create', name: 'Nueva sesión', action: 'create', icon: 'Add' },
          { id: 'export', name: 'Exportar', action: 'export', icon: 'CloudDownload' },
        ],
      },
    ],
  },
  {
    id: 'orders',
    name: 'Ventas',
    icon: 'Receipt',
    order: 3,
    menus: [
      {
        id: 'sales-management',
        name: 'Gestión de Ventas',
        path: '/dashboard/orders',
        order: 1,
        bindViewRowToMenuVisibility: true,
        flowSectionTitle: 'Flujo de venta',
        mainButtons: [{ id: 'add', name: 'Añadir', action: 'add', icon: 'Add' }],
        salesFlowActions: [
          {
            // UI label for this permission; permission_catalog.display_name in DB is not used by the role wizard.
            id: 'modify_sale_date',
            name: 'Modificar fecha y hora de venta',
            action: 'modify_sale_date',
            icon: 'Event',
            description:
              'Permite fijar la fecha y hora comercial al confirmar una nueva venta o al procesar un pago.',
          },
          {
            id: 'mixed_sell',
            name: 'Venta multi-sucursal',
            action: 'mixed_sell',
            icon: 'Store',
            description:
              'Permite armar ventas con piezas de otras sucursales (catálogo remoto), confirmar la venta y completarla cuando la mercadería llega. Genera solicitudes de traslado automáticas.',
          },
          {
            id: 'change_seller',
            name: 'Cambiar vendedor',
            action: 'change_seller',
            icon: 'Person',
            description:
              'Permite elegir, dentro de la misma sucursal, qué usuario figura como vendedor al registrar una nueva venta (sin cerrar sesión).',
          },
        ],
        rowActions: [
          { id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' },
          { id: 'pay', name: 'Procesar pago', action: 'pay', icon: 'Payment' },
          {
            id: 'edit',
            name: 'Editar / Entregar',
            action: 'edit',
            icon: 'LocalShipping',
          },
          { id: 'return', name: 'Devolución', action: 'return', icon: 'Undo' },
          { id: 'exchange', name: 'Intercambio', action: 'exchange', icon: 'SwapHoriz' },
        ],
      },
      {
        id: 'sales-catalog',
        name: 'Catálogo de Ventas',
        path: '/dashboard/orders/catalog',
        order: 2,
      },
      {
        id: 'sales-by-piece',
        name: 'Ventas por Pieza',
        path: '/dashboard/orders/sales-by-piece',
        order: 3,
        granularity: 'menu-only',
        rowActions: [{ id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' }],
      },
      {
        id: 'seller-management',
        name: 'Gestión de Vendedores',
        path: '/dashboard/orders/sellers',
        order: 4,
        granularity: 'menu-only',
        rowActions: [{ id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' }],
      },
    ],
  },
  {
    id: 'crm',
    name: 'Clientes',
    icon: 'Contacts',
    order: 3.5,
    menus: [
      {
        id: 'client-management',
        name: 'Gestión de clientes',
        path: '/dashboard/crm/clients',
        order: 1,
        bindViewRowToMenuVisibility: true,
        mainButtons: [
          { id: 'add', name: 'Añadir', action: 'add', icon: 'Add' },
          { id: 'export', name: 'Exportar', action: 'export', icon: 'CloudDownload' },
          { id: 'delete', name: 'Eliminar', action: 'delete', icon: 'DeleteOutline' },
        ],
        rowActions: [
          { id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' },
          { id: 'edit', name: 'Editar', action: 'edit', icon: 'Edit' },
          { id: 'delete_row', name: 'Eliminar', action: 'delete', icon: 'Delete' },
        ],
      },
    ],
  },
  {
    id: 'reports',
    name: 'Reportes',
    icon: 'Description',
    order: 4,
    menus: [
      {
        id: 'product-history',
        name: 'Historial de Movimientos',
        path: '/dashboard/reports/product-history',
        order: 1,
        granularity: 'menu-only', // Solo visibilidad del menú
        rowActions: [{ id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' }],
      },
      {
        id: 'statistics',
        name: 'Estadísticas',
        path: '/dashboard/reports/statistics',
        order: 2,
        granularity: 'menu-only',
        rowActions: [{ id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' }],
      },
    ],
  },
  {
    id: 'settings',
    name: 'Ajustes',
    icon: 'Settings',
    order: 5,
    menus: [
      {
        id: 'properties',
        name: 'Propiedades',
        path: '/dashboard/settings/properties',
        order: 1,
        bindViewRowToMenuVisibility: true,
        mainButtons: [{ id: 'add', name: 'Añadir', action: 'add', icon: 'Add' }],
        rowActions: [
          { id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' },
          { id: 'edit', name: 'Editar', action: 'edit', icon: 'Edit' },
          { id: 'delete', name: 'Eliminar', action: 'delete', icon: 'Delete' },
        ],
      },
      {
        id: 'roles-management',
        name: 'Gestión de Roles',
        path: '/dashboard/settings/roles',
        order: 2,
        granularity: 'menu-only', // Todo o nada: con view se gestiona el menú completo
        rowActions: [{ id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' }],
      },
      {
        id: 'users-management',
        name: 'Gestión de Usuarios',
        path: '/dashboard/settings/users',
        order: 3,
        granularity: 'menu-only', // Todo o nada: con view se gestiona el menú completo
        rowActions: [{ id: 'view', name: 'Ver', action: 'view', icon: 'Visibility' }],
      },
    ],
  },
  {
    id: 'system',
    name: 'Sistema',
    icon: 'Computer',
    order: 6,
    menus: [
      {
        id: 'system-config',
        name: 'Configuración del Sistema',
        path: '/dashboard/system',
        order: 1,
        mainButtons: [
          {
            id: 'select_theme',
            name: 'Cambiar tema (claro / oscuro)',
            action: 'select_theme',
            icon: 'DarkMode',
          },
        ],
      },
    ],
  },
];

/**
 * Helper para obtener un módulo por ID
 */
export const getModuleById = (moduleId: string): ModuleConfig | undefined => {
  return SYSTEM_STRUCTURE.find((module) => module.id === moduleId);
};

/**
 * Helper para obtener un menú por ID
 */
export const getMenuById = (menuId: string): MenuConfig | undefined => {
  for (const module of SYSTEM_STRUCTURE) {
    const menu = module.menus.find((m) => m.id === menuId);
    if (menu) return menu;
  }
  return undefined;
};

/**
 * Helper para obtener todos los módulos ordenados
 */
export const getOrderedModules = (): ModuleConfig[] => {
  return [...SYSTEM_STRUCTURE].sort((a, b) => a.order - b.order);
};

/** Menú catálogo de productos. */
export const PRODUCT_CATALOG_MENU_ID = 'product-catalog';

/** Id de la acción de fila "Ver" en el wizard (API: view_row). */
export const MENU_VIEW_ROW_ACTION_ID = 'view';

/** Botón del wizard que activa gestión completa del catálogo. */
export const PRODUCT_CATALOG_BUNDLE_TOGGLE_ID = 'add';

/** Permisos API incluidos al marcar Añadir (gestión completa). */
export const PRODUCT_CATALOG_BUNDLE_BUTTON_ACTIONS = ['add', 'export', 'delete'] as const;
export const PRODUCT_CATALOG_BUNDLE_ROW_ACTIONS = ['edit', 'duplicate'] as const;

const PRODUCT_CATALOG_MANAGEMENT_ACTIONS = new Set<string>([
  ...PRODUCT_CATALOG_BUNDLE_BUTTON_ACTIONS,
  ...PRODUCT_CATALOG_BUNDLE_ROW_ACTIONS,
]);

export const isProductCatalogMenu = (menuId: string): boolean => menuId === PRODUCT_CATALOG_MENU_ID;

export const isBundleAddMenu = (menuId: string): boolean =>
  getMenuById(menuId)?.granularity === 'bundle-add';

export const isProductCatalogManagementAction = (action: string): boolean =>
  PRODUCT_CATALOG_MANAGEMENT_ACTIONS.has(action);

export const menuBindsViewRowToMenuVisibility = (menuId: string): boolean =>
  getMenuById(menuId)?.bindViewRowToMenuVisibility === true;

/**
 * Menú visible ⇒ acción de fila Ver activa (solo menús con bindViewRowToMenuVisibility).
 */
export const syncBoundViewRowForMenuPermissions = (
  menuId: string,
  menu: { visible: boolean; rowActions: Record<string, boolean> },
): void => {
  if (!menuBindsViewRowToMenuVisibility(menuId)) return;
  if (
    menu.visible &&
    Object.prototype.hasOwnProperty.call(menu.rowActions, MENU_VIEW_ROW_ACTION_ID)
  ) {
    menu.rowActions[MENU_VIEW_ROW_ACTION_ID] = true;
  }
};
